import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { usePermissions } from "../../../../common/infrastructure/hooks/usePermissions";
import { useAppContext } from "../../../../AppContext";
import { IPlannerEventModel } from "../models/plannerEventModel";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import {
  constructCalendarEventPreviewHref,
  constructEmployeeHref,
  constructPlannerEventEditHref,
  getDefaultRoutePath,
  routePaths,
} from "../../../../common/infrastructure/routes/routePaths";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAddAdditionalLayoutButton } from "../../../../common/infrastructure/hooks/useAddAdditionalLayoutButton";
import { IconType } from "../../../../common/icons/Icon";
import { IPlannerEventUserModel } from "../models/plannerEventUserModel";
import { dateUtility } from "../../../../common/infrastructure/utilities/dateUtility";
import {
  formatDuration,
  isMultiDayDuration,
} from "../../../../common/infrastructure/utilities/durationUtility";
import { EventType, PaymentTypes } from "../../../../common/enums";
import { copyToClipboard } from "../../../../common/utilities/clipboardUtility";
import {
  getSuccessToast,
  getErrorToast,
} from "../../../../common/infrastructure/utilities/toastUtility";
import { IStudioSettingsModel } from "../../../../common/models/IStudioSettingsModel";

export const usePlannerEvent = () => {
  const { id: eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userHavePermissionsByRoute } = usePermissions();
  const { selectedStudioId, currentUser } = useAppContext();
  const [selectedUserEventDetailsId, setSelectedUserEventDetailsId] =
    useState(0);
  const [paymentDialogUserEventId, setPaymentDialogUserEventId] = useState(0);
  const [isMusicDialogOpen, setIsMusicDialogOpen] = useState(false);

  const { data: event, isLoading } = useApiQuery<IPlannerEventModel>({
    queryKey: [endpointsMetadata.planner.getEvent, eventId],
    endpoint: `${endpointsMetadata.planner.getEvent}?eventId=${eventId}`,
    enabled: eventId !== undefined,
    disableErrorToast: false,
    retry: false,
    globalIsLoading: false,
  });

  const { data: studioSettings } = useApiQuery<IStudioSettingsModel>({
    queryKey: [endpointsMetadata.studio.getStudioSettings, selectedStudioId],
    endpoint: `${endpointsMetadata.studio.getStudioSettings}`,
    enabled: selectedStudioId > 0,
    globalIsLoading: false,
  });

  // Open reservation dialog from URL parameter when data is ready
  useEffect(() => {
    if (isLoading || !event) return;

    const reservationIdParam = searchParams.get("reservationId");
    if (reservationIdParam) {
      const reservationId = parseInt(reservationIdParam, 10);
      if (!isNaN(reservationId) && reservationId > 0) {
        setSelectedUserEventDetailsId(reservationId);
      }
    }
  }, [searchParams, isLoading, event]);

  const openReservationDialog = useCallback(
    (userEventId: number) => {
      setSelectedUserEventDetailsId(userEventId);
      // replace (not push) so opening/closing the reservation dialog does not
      // add extra browser-history entries the back button would have to skip.
      setSearchParams(
        (prev) => {
          prev.set("reservationId", userEventId.toString());
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const closeReservationDialog = useCallback(() => {
    setSelectedUserEventDetailsId(0);
    setSearchParams(
      (prev) => {
        prev.delete("reservationId");
        return prev;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const openMusicDialog = useCallback(() => {
    setIsMusicDialogOpen(true);
  }, []);

  const closeMusicDialog = useCallback(() => {
    setIsMusicDialogOpen(false);
  }, []);

  const backToCalendar = () => {
    navigate(getDefaultRoutePath(currentUser));
  };

  const userHavePermissionToSignUpToEvent = userHavePermissionsByRoute(
    routePaths.plannerEventSignUp
  );

  useAddAdditionalLayoutButton({
    iconType: IconType.EditIcon,
    href: constructPlannerEventEditHref(event?.id ?? 0),
    rawPath: routePaths.plannerEventEditSignUp,
    validator: !!event && !event.isDeleted,
  });

  const onEventArrivalConfirmationClick = (item: IPlannerEventUserModel) => {
    navigate(
      routePaths.plannerEventArrivalConfirmation.replace(
        ":userEventId",
        item.userEventId.toString()
      )
    );
  };

  const onEventLinkGenerationIniitalized = async () => {
    const link = constructCalendarEventPreviewHref(
      selectedStudioId,
      Number(eventId),
      new Date(event?.date ?? "")
    );

    const success = await copyToClipboard(link);

    if (success) {
      getSuccessToast("Линкът е копиран в клипборда!");
    } else {
      getErrorToast("Грешка при копиране на линка");
    }
  };

  const onInstructorClick = () => {
    navigate(constructEmployeeHref(event?.employeeId ?? 0));
  };

  // For a full day or longer, the end clock time equals the start (e.g. 16:00-16:00),
  // which is misleading — show just the start; the duration row carries the span. Sub-day
  // events keep the "16:00 - 19:00" range.
  const dateTimeText = isMultiDayDuration(event?.durationInMinutes)
    ? `${dateUtility.formatDate(event?.date)}, ${event?.startTime}`
    : `${dateUtility.formatDate(event?.date)}, ${event?.startTime}-${
        event?.endTime
      }`;

  const durationText = formatDuration(event?.durationInMinutes);

  const reservationsCountText = `${event?.takenSpaces}/${
    event?.totalSpaces ?? "∞"
  }`;

  const notGroupEvent = useMemo(() => event?.type !== EventType.Group, [event]);
  const tenantSupportsMultisport = !!studioSettings?.paymentTypes.find(
    (x) => x.paymentType === PaymentTypes.Multisport
  );

  const usersListName = notGroupEvent
    ? "Записан потребител"
    : "Записани потребители";

  const isIndividualClass = event?.type === EventType.Individual;

  const hiddenClassTooltip = isIndividualClass
    ? "Това събитие не се показва в портала на потребителите, понеже е индивидуален клас."
    : "Това събитие не се вижда в портала на потребителите, понеже е маркиран да не се показва. Това може да бъде променено от панела за редакция на класа";

  return {
    event,
    isLoading,
    userHavePermissionToSignUpToEvent,
    isGroupEvent: !notGroupEvent,
    isIndividualEvent: isIndividualClass,
    eventId,
    dateTimeText,
    durationText,
    reservationsCountText,
    tenantSupportsMultisport,
    usesHalls: studioSettings?.usesHalls,
    usersListName,
    hiddenClassTooltip,
    studioSettings,
    selectedUserEventDetailsId,
    openReservationDialog,
    closeReservationDialog,
    paymentDialogUserEventId,
    setPaymentDialogUserEventId,
    onInstructorClick,
    onEventArrivalConfirmationClick,
    onEventLinkGenerationIniitalized,
    navigate,
    backToCalendar,
    isMusicDialogOpen,
    openMusicDialog,
    closeMusicDialog,
  };
};
